import type {
  BridgeChartIntelligenceMarker,
  BridgeChartIntelligenceMarkerKind,
  BridgeChartMarker,
  NormalizedBridgeChartData
} from "../types/data";
import type { ChartLayout } from "../renderers/layout";
import type { PriceScale } from "../scales/priceScale";
import { xForIndex } from "../scales/timeScale";

export interface MarkerCoordinates {
  marker: BridgeChartMarker;
  x: number;
  y: number;
  down: boolean;
}

const markerKindOrder: readonly BridgeChartIntelligenceMarkerKind[] = [
  "Strategy", "Backtest", "WalkForward", "CompositeAi", "Codal", "MaterialRisk"
];

export interface MarkerCollision {
  marker: BridgeChartIntelligenceMarker;
  collisionIndex: number;
  collisionCount: number;
}

export function normalizeIntelligenceMarkers(markers: readonly BridgeChartIntelligenceMarker[] = []): BridgeChartIntelligenceMarker[] {
  const candidates = markers.map(marker => ({
    ...marker,
    markerId: String(marker.markerId || "").trim(),
    date: String(marker.date || "").trim(),
    direction: String(marker.direction || "").trim(),
    labelEn: String(marker.labelEn || "").trim(),
    labelFa: String(marker.labelFa || "").trim(),
    source: {
      ...marker.source,
      sourceType: String(marker.source?.sourceType || "").trim(),
      sourceId: String(marker.source?.sourceId || "").trim(),
      sourceRevision: String(marker.source?.sourceRevision || "").trim(),
      runId: marker.source?.runId ? String(marker.source.runId).trim() : null
    },
    evidence: {
      ...marker.evidence,
      evidenceId: String(marker.evidence?.evidenceId || "").trim(),
      summary: String(marker.evidence?.summary || "").trim(),
      provenance: [...(marker.evidence?.provenance || [])].map(value => String(value).trim()).filter(Boolean),
      score: finiteOrNull(marker.evidence?.score),
      confidence: finiteOrNull(marker.evidence?.confidence)
    },
    eventProvenance: {
      ...marker.eventProvenance,
      kind: marker.eventProvenance?.kind,
      sourceType: String(marker.eventProvenance?.sourceType || "").trim(),
      sourceId: String(marker.eventProvenance?.sourceId || "").trim(),
      sourceRevision: String(marker.eventProvenance?.sourceRevision || "").trim(),
      observedAt: String(marker.eventProvenance?.observedAt || "").trim(),
      explanationId: marker.eventProvenance?.explanationId ? String(marker.eventProvenance.explanationId).trim() : null
    }
  }));

  const idCounts = new Map<string, number>();
  candidates.forEach(marker => idCounts.set(marker.markerId, (idCounts.get(marker.markerId) || 0) + 1));
  return candidates
    .filter(isAcceptedMarker)
    .filter(marker => idCounts.get(marker.markerId) === 1)
    .sort(compareMarkers);
}

export function resolveMarkerCollisions(markers: readonly BridgeChartIntelligenceMarker[]): MarkerCollision[] {
  const normalized = normalizeIntelligenceMarkers(markers);
  const dateCounts = new Map<string, number>();
  normalized.forEach(marker => dateCounts.set(marker.date, (dateCounts.get(marker.date) || 0) + 1));
  const dateIndexes = new Map<string, number>();
  return normalized.map(marker => {
    const collisionIndex = dateIndexes.get(marker.date) || 0;
    dateIndexes.set(marker.date, collisionIndex + 1);
    return { marker, collisionIndex, collisionCount: dateCounts.get(marker.date) || 1 };
  });
}

export function evidencePanelForMarker(
  markerId: string,
  markers: readonly BridgeChartIntelligenceMarker[]
): BridgeChartIntelligenceMarker | null {
  const matches = normalizeIntelligenceMarkers(markers).filter(marker => marker.markerId === markerId);
  return matches.length === 1 ? matches[0] : null;
}

export function mapMarker(
  marker: BridgeChartMarker,
  data: NormalizedBridgeChartData,
  start: number,
  end: number,
  layout: ChartLayout,
  priceScale: PriceScale,
  offset: number
): MarkerCoordinates | null {
  const index = data.dateIndex[marker.date];
  if (index === undefined || index < start || index > end) return null;
  const bar = data.bars[index];
  const down = marker.direction === "Negative";
  return {
    marker,
    x: xForIndex(layout, start, end, index),
    y: priceScale.y(down ? bar.high : bar.low) + (down ? -offset : offset),
    down
  };
}

function isAcceptedMarker(marker: BridgeChartIntelligenceMarker): boolean {
  return markerKindOrder.includes(marker.kind) &&
    /^\d{4}-\d{2}-\d{2}$/.test(marker.date) &&
    marker.markerId.length > 0 && marker.direction.length > 0 &&
    marker.labelEn.length > 0 && marker.labelFa.length > 0 &&
    marker.source.sourceType.length > 0 && marker.source.sourceId.length > 0 &&
    marker.source.sourceRevision.length > 0 && marker.evidence.evidenceId.length > 0 &&
    marker.evidence.summary.length > 0 && marker.evidence.provenance.length > 0 &&
    marker.eventProvenance.kind === marker.kind &&
    marker.eventProvenance.sourceType === marker.source.sourceType &&
    marker.eventProvenance.sourceId === marker.source.sourceId &&
    marker.eventProvenance.sourceRevision === marker.source.sourceRevision &&
    isCanonicalObservedAt(marker.eventProvenance.observedAt);
}

function isCanonicalObservedAt(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(value)) return false;
  const parsed = new Date(value);
  return Number.isFinite(parsed.valueOf()) && parsed.toISOString().replace(".000Z", "Z") === value;
}

function compareMarkers(left: BridgeChartIntelligenceMarker, right: BridgeChartIntelligenceMarker): number {
  return left.date.localeCompare(right.date) ||
    markerKindOrder.indexOf(left.kind) - markerKindOrder.indexOf(right.kind) ||
    left.source.sourceId.localeCompare(right.source.sourceId) ||
    left.markerId.localeCompare(right.markerId);
}

function finiteOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

// Namespace members keep the focused contract testable through the existing public mapMarker export.
export namespace mapMarker {
  export const normalize = normalizeIntelligenceMarkers;
  export const collisions = resolveMarkerCollisions;
  export const evidencePanel = evidencePanelForMarker;
}
